import React, { createContext, useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';
import { Outlet } from 'react-router-dom';

// import Loading from 'components/atoms/Loading';
import { i18ChangeLanguage } from 'i18n';
import { getChatBotInfoService } from 'services/accountConversations';
import { ChatBotInfoResponseTypes, DataError } from 'services/accountConversations/types';
import { getLocalStorage } from 'services/common/storage';
import { AGENT_TYPE, AUTHENTICATION_TYPE, LOCAL_STORAGE_CHATBOT } from 'utils/constants';
// import { boolean } from 'yup';

type ContextChatbotProps = {
  getInfoChatBot: () => void;
  getInfoChatBotAsync: () => Promise<ChatBotInfoResponseTypes>;
  isLoadingChatBot?: boolean;
} & ChatBotInfoResponseTypes;

export const ContextChatbot = createContext<ContextChatbotProps>({
  avatar: '',
  mainColor: '',
  name: '',
  shortDescription: '',
  getInfoChatBot: () => { },
  getInfoChatBotAsync: () => Promise.resolve({} as ChatBotInfoResponseTypes),
  type: AGENT_TYPE.ANONYMOUS,
  verifyType: AUTHENTICATION_TYPE.EMAIL_OTP,
  bgColorFirst: '',
  bgColorSecond: '',
  bgFiles: [],
  bgType: 0,
  bgImage: '',
  supportedLanguageCode: 'vi',
  welcomeMessage: '',
  onboardMessage: '',
  sessionData: {
    idleTime: 0,
    notifyExpiryIdleTime: 0,
    notifyExpiryMaxLifeTime: 0,
    idleTimeWarningMessage: '',
    maxLifeWarningMessage: '',
    buttonTextWarningMessage: '',
    expiresAt: ''
  },
  processMsgType: 0,
  processCustomMsg: '',
  suggestedMsg: [],
  bubbleAvatar: '',
  bubbleSize: '',
  teaserMsg: '',
  tierData: {
    allowChatUpload: false,
    allowT2s: false,
    allowS2t: false,
  },
  isError: false,
  errorChatBot: undefined,
  isPro: false,
});

type MainlayoutChatbotProps = {
  children?: React.ReactNode;
};

const MainlayoutChatbot: React.FC<MainlayoutChatbotProps> = ({
  children
}) => {
  const {
    data: chatBotInfo,
    mutate: chatBotInfoMutate,
    mutateAsync: chatBotInfoMutateAsync,
    isError: isErrorChatBot,
    error: errorChatBot,
    isLoading: isLoadingChatBot
  } = useMutation<ChatBotInfoResponseTypes, DataError>(['get-chat-bot-info'], () => getChatBotInfoService());

  const botId = (window as any).currentBotPath;

  const valueContext = useMemo(
    () => ({
      avatar: chatBotInfo?.avatar || '',
      mainColor: chatBotInfo?.mainColor || '',
      shortDescription: chatBotInfo?.shortDescription || '',
      name: chatBotInfo?.name || '',
      getInfoChatBot: () => chatBotInfoMutate(),
      getInfoChatBotAsync: () => chatBotInfoMutateAsync(),
      type: chatBotInfo?.type || 1,
      verifyType: chatBotInfo?.verifyType || 1,
      bgColorFirst: chatBotInfo?.bgColorFirst || '',
      bgColorSecond: chatBotInfo?.bgColorSecond || '',
      bgType: chatBotInfo?.bgType || 0,
      bgImage: chatBotInfo?.bgImage || '',
      welcomeMessage: chatBotInfo?.welcomeMessage || '',
      onboardMessage: chatBotInfo?.onboardMessage || '',
      bgFiles: chatBotInfo?.bgFiles || [],
      supportedLanguageCode: chatBotInfo?.supportedLanguageCode || 'vi',
      sessionData: chatBotInfo?.sessionData || {
        idleTime: 0,
        notifyExpiryIdleTime: 0,
        notifyExpiryMaxLifeTime: 0,
        idleTimeWarningMessage: '',
        maxLifeWarningMessage: '',
        buttonTextWarningMessage: '',
        expiresAt: ''
      },
      processMsgType: chatBotInfo?.processMsgType || 0,
      processCustomMsg: chatBotInfo?.processCustomMsg || '',
      suggestedMsg: chatBotInfo?.suggestedMsg || [],
      bubbleAvatar: chatBotInfo?.bubbleAvatar || '',
      bubbleSize: chatBotInfo?.bubbleSize || '',
      teaserMsg: chatBotInfo?.teaserMsg || '',
      tierData: {
        allowChatUpload: chatBotInfo?.tierData?.allowChatUpload || false,
        allowT2s: chatBotInfo?.tierData?.allowT2s || false,
        allowS2t: chatBotInfo?.tierData?.allowS2t || false,
      },
      errorChatBot: errorChatBot || undefined,
      isError: isErrorChatBot,
      isLoadingChatBot,
      isPro: chatBotInfo?.isPro || false
    }),
    [chatBotInfo?.avatar, chatBotInfo?.mainColor,
      isErrorChatBot, chatBotInfo?.name, chatBotInfoMutate, isLoadingChatBot]
  );

  useEffect(() => {
    if (chatBotInfo?.supportedLanguageCode) {
      i18ChangeLanguage(chatBotInfo?.supportedLanguageCode);
    }
  }, [chatBotInfo?.supportedLanguageCode]);

  useEffect(() => {
    const accessTokenChatBot = getLocalStorage(LOCAL_STORAGE_CHATBOT.ACCESSTOKEN_CHATBOT + botId) || '';
    if (accessTokenChatBot) {
      chatBotInfoMutate();
    }
  }, [botId, chatBotInfoMutate]);

  return (
    <ContextChatbot.Provider
      value={valueContext}
    >
      {children}
      <Outlet />
    </ContextChatbot.Provider>
  );
};

export default MainlayoutChatbot;
